import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { formatDateDDMMYYYY, formatDateWithWeekday } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Download, Upload, AlertTriangle, 
  CheckCircle2, XCircle, Users, MapPin, RefreshCw, FileSpreadsheet,
  Eye, Filter, Loader2
} from 'lucide-react';

const PendingAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [notVisitedParks, setNotVisitedParks] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('all');
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [summary, setSummary] = useState({ total: 0, marked: 0, pending: 0 });
  
  // Bulk date selection states
  const [showBulkMode, setShowBulkMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  
  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadPolling, setUploadPolling] = useState(false);
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  // Initial setup - set default date once
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  }, [authLoading, user, userType]);

  // Fetch on date/zone change (user actions)
  useEffect(() => {
    if (selectedDate && hasFetched.current) {
      fetchNotVisitedParks();
    }
  }, [selectedDate, selectedZone]);

  const fetchNotVisitedParks = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getNotVisitedParks(selectedDate, selectedZone);
      setNotVisitedParks(response.data.parks || []);
      setZones(response.data.available_zones || []);
      setSummary(response.data.summary || { total: 0, marked: 0, pending: 0 });
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchNotVisitedParks(true);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await adminAPI.downloadNotVisitedTemplate(selectedDate, selectedZone);
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Not_Visited_Parks_${selectedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadAttendance = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('date', selectedDate);
      
      const response = await adminAPI.uploadNotVisitedAttendance(formData);
      
      toast.success(response.data.message || 'Attendance uploaded successfully');
      setUploadFile(null);
      fetchNotVisitedParks(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attendance');
    } finally {
      setUploading(false);
    }
  };

  // Get date options - not needed anymore, using calendar input
  const getDateOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 90; i++) { // Extended to 90 days
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const value = d.toISOString().split('T')[0];
      const label = formatDateDDMMYYYY(value);
      options.push({ value, label: i === 0 ? `Today (${label})` : label });
    }
    return options;
  };

  // Get extended date options for bulk mode (last 30 days)
  const getBulkDateOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const value = d.toISOString().split('T')[0];
      const label = formatDateDDMMYYYY(value);
      options.push({ value, label, dayOfWeek: d.getDay() });
    }
    return options;
  };

  // Toggle date selection for bulk mode
  const toggleDateSelection = (dateValue) => {
    setSelectedDates(prev => 
      prev.includes(dateValue) 
        ? prev.filter(d => d !== dateValue)
        : [...prev, dateValue].sort()
    );
  };

  // Download bulk template for multiple dates
  const handleBulkDownload = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }
    
    setBulkDownloading(true);
    try {
      // Download template for each date and combine
      const allParks = [];
      
      for (const date of selectedDates) {
        try {
          const response = await adminAPI.getNotVisitedParks(date, selectedZone);
          const parks = response.data.parks || [];
          console.log(`Date ${date}: Found ${parks.length} parks`);
          parks.forEach(park => {
            // Use manpower_list (the actual field name from API)
            const employees = park.manpower_list || park.employees || [];
            if (employees.length === 0) {
              // If no employees, still add the park with placeholder
              allParks.push({
                date: date,
                park_id: park.park_id,
                park_name: park.park_name,
                zone: park.zone,
                division: park.division || '',
                employee_id: '',
                employee_name: 'No employees assigned',
                category: '',
                attendance_yes_no: ''
              });
            } else {
              employees.forEach(emp => {
                allParks.push({
                  date: date,
                  park_id: park.park_id,
                  park_name: park.park_name,
                  zone: park.zone,
                  division: park.division || '',
                  employee_id: emp.employee_id || '',
                  employee_name: emp.employee_name || emp.name || '',
                  category: emp.category || '',
                  attendance_yes_no: ''
                });
              });
            }
          });
        } catch (dateError) {
          console.error(`Error fetching date ${date}:`, dateError);
        }
      }
      
      console.log(`Total records to download: ${allParks.length}`);
      
      if (allParks.length === 0) {
        toast.info('No pending attendance records found for selected dates');
        setBulkDownloading(false);
        return;
      }
      
      // Create Excel file
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(allParks);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Attendance');
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 12 }, // date
        { wch: 10 }, // park_id
        { wch: 30 }, // park_name
        { wch: 8 },  // zone
        { wch: 10 }, // division
        { wch: 12 }, // employee_id
        { wch: 25 }, // employee_name
        { wch: 15 }, // category
        { wch: 18 }  // attendance_yes_no
      ];
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bulk_Pending_Attendance_${selectedDates[0]}_to_${selectedDates[selectedDates.length-1]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded template for ${selectedDates.length} dates (${allParks.length} records)`);
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Failed to download bulk template: ' + (error.message || 'Unknown error'));
    } finally {
      setBulkDownloading(false);
    }
  };

  // Upload bulk attendance for multiple dates with progress tracking
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setBulkUploading(true);
    setUploadProgress({ status: 'uploading', message: 'Uploading file...' });
    
    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      
      const response = await adminAPI.uploadNotVisitedAttendance(formData);
      const { upload_id, success_count, error_count, total_rows } = response.data;
      
      setUploadProgress({
        status: 'completed',
        message: `Completed: ${success_count} records uploaded`,
        success_count,
        error_count,
        total_rows
      });
      
      toast.success(`Successfully marked attendance for ${success_count} employees`);
      setBulkUploadFile(null);
      
      // Clear progress after 5 seconds
      setTimeout(() => setUploadProgress(null), 5000);
      
      fetchNotVisitedParks(); // Refresh data
    } catch (error) {
      console.error('Bulk upload error:', error);
      setUploadProgress({
        status: 'error',
        message: error.response?.data?.detail || 'Failed to upload bulk attendance'
      });
      toast.error(error.response?.data?.detail || 'Failed to upload bulk attendance');
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-800 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Pending Attendance
              </h1>
              <p className="text-sm opacity-90">Mark attendance for parks not visited</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-pending-attendance-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Mode Toggle */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Mode</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkMode(false)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  !showBulkMode 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Single Date
              </button>
              <button
                onClick={() => setShowBulkMode(true)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  showBulkMode 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Bulk Dates
              </button>
            </div>
          </div>

          {/* Bulk Mode - Multi-date Selection */}
          {showBulkMode && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-700">Select Multiple Dates (Last 30 Days)</h4>
                <span className="text-sm text-orange-600 font-bold">
                  {selectedDates.length} dates selected
                </span>
              </div>
              
              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => {
                    const march1_3 = ['2026-03-01', '2026-03-02', '2026-03-03'];
                    setSelectedDates(march1_3);
                  }}
                  className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm font-semibold"
                >
                  Mar 1-3
                </button>
                <button
                  onClick={() => {
                    const lastWeek = getBulkDateOptions().slice(0, 7).map(d => d.value);
                    setSelectedDates(lastWeek);
                  }}
                  className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm font-semibold"
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => setSelectedDates([])}
                  className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-sm font-semibold"
                >
                  Clear All
                </button>
              </div>
              
              {/* Date Grid */}
              <div className="grid grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2 mb-4">
                {getBulkDateOptions().map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => toggleDateSelection(opt.value)}
                    className={`p-2 rounded-lg text-xs font-semibold transition-colors ${
                      selectedDates.includes(opt.value)
                        ? 'bg-orange-600 text-white'
                        : opt.dayOfWeek === 0 
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    title={opt.label}
                  >
                    <div>{opt.value.split('-')[2]}</div>
                    <div className="text-[10px] opacity-75">{opt.label.split(',')[0]}</div>
                  </button>
                ))}
              </div>
              
              {/* Selected Dates Display */}
              {selectedDates.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-semibold text-orange-800 mb-2">Selected Dates:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedDates.map(date => (
                      <span 
                        key={date}
                        className="px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs font-semibold"
                      >
                        {date}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Bulk Actions */}
              <div className="flex flex-wrap gap-4 items-center">
                {/* Zone Filter for Bulk */}
                <div className="flex-1 min-w-[150px] max-w-[200px]">
                  <select
                    value={selectedZone}
                    onChange={(e) => setSelectedZone(e.target.value)}
                    className="w-full h-10 px-3 border-2 border-slate-300 rounded-lg text-sm"
                  >
                    <option value="all">All Zones</option>
                    {zones.map(zone => (
                      <option key={zone} value={zone}>Zone {zone}</option>
                    ))}
                  </select>
                </div>
                
                {/* Download Bulk Template */}
                <button
                  onClick={handleBulkDownload}
                  disabled={bulkDownloading || selectedDates.length === 0}
                  className="h-10 px-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {bulkDownloading ? 'Downloading...' : 'Download Bulk Template'}
                </button>
                
                {/* Upload Bulk Attendance */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setBulkUploadFile(e.target.files[0])}
                    className="hidden"
                    id="bulk-upload-input"
                  />
                  <label
                    htmlFor="bulk-upload-input"
                    className="h-10 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg font-bold flex items-center gap-2 cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    {bulkUploadFile ? bulkUploadFile.name.slice(0, 20) + '...' : 'Choose File'}
                  </label>
                  {bulkUploadFile && (
                    <button
                      onClick={handleBulkUpload}
                      disabled={bulkUploading}
                      className="h-10 px-4 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                    >
                      {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {bulkUploading ? 'Uploading...' : 'Upload Bulk'}
                    </button>
                  )}
                </div>
                
                {/* Upload Progress Indicator */}
                {uploadProgress && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    uploadProgress.status === 'completed' ? 'bg-green-50 border border-green-200' :
                    uploadProgress.status === 'error' ? 'bg-red-50 border border-red-200' :
                    'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {uploadProgress.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : uploadProgress.status === 'error' ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      )}
                      <span className={`font-medium ${
                        uploadProgress.status === 'completed' ? 'text-green-700' :
                        uploadProgress.status === 'error' ? 'text-red-700' :
                        'text-blue-700'
                      }`}>
                        {uploadProgress.message}
                      </span>
                    </div>
                    {uploadProgress.status === 'completed' && uploadProgress.error_count > 0 && (
                      <p className="mt-1 text-sm text-orange-600">
                        {uploadProgress.error_count} records had errors
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Single Date Mode - Filters */}
        {!showBulkMode && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* Date Selector - Calendar Input */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Select Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full h-12 pl-10 pr-4 border-2 border-slate-300 rounded-lg focus:border-orange-600 focus:ring-0 bg-white cursor-pointer"
                  data-testid="date-select"
                />
              </div>
            </div>
            
            {/* Zone Filter */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Zone</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  value={selectedZone}
                  onChange={(e) => setSelectedZone(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border-2 border-slate-300 rounded-lg focus:border-orange-600 focus:ring-0 appearance-none bg-white"
                  data-testid="zone-filter"
                >
                  <option value="all">All Zones</option>
                  {zones.map(zone => (
                    <option key={zone} value={zone}>Zone {zone}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={fetchNotVisitedParks}
              disabled={loading}
              className="h-12 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg font-bold flex items-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {/* Parks Summary */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{summary.total}</div>
                <div className="text-xs text-slate-500">Parks Not Visited</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-red-600">{summary.pending}</div>
                <div className="text-xs text-slate-500">Parks Pending</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-green-600">{summary.marked}</div>
                <div className="text-xs text-slate-500">Parks Marked</div>
              </div>
            </div>
          </div>
          
          {/* Manpower Summary */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-blue-600">{summary.total_manpower || 0}</div>
                <div className="text-xs text-slate-500">Total Manpower</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-red-600">{summary.manpower_pending || 0}</div>
                <div className="text-xs text-slate-500">Manpower Pending</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-green-600">{summary.manpower_marked || 0}</div>
                <div className="text-xs text-slate-500">Manpower Marked</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Download Template */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="font-bold text-lg">Step 1: Download Template</h3>
                <p className="text-sm text-slate-500">Get list of not-visited parks with employees</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Download Excel file containing all parks not visited on {formatDateDDMMYYYY(selectedDate)} along with their employee list. 
              Mark "Yes" or "No" in the Attendance column for each employee.
            </p>
            <button
              onClick={handleDownloadTemplate}
              disabled={downloading || notVisitedParks.length === 0}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="download-template-btn"
            >
              {downloading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-5 h-5" />
                  Download Template ({notVisitedParks.length} parks)
                </>
              )}
            </button>
          </div>

          {/* Upload Attendance */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Upload className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-bold text-lg">Step 2: Upload Attendance</h3>
                <p className="text-sm text-slate-500">Upload the filled template</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              After marking attendance in the Excel file, upload it here. 
              System will automatically mark Present/Absent for all employees.
            </p>
            <div className="space-y-3">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full h-12 px-4 border-2 border-dashed border-slate-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-100 file:text-green-700 file:font-bold hover:border-green-500"
                data-testid="upload-file-input"
              />
              <button
                onClick={handleUploadAttendance}
                disabled={uploading || !uploadFile}
                className="w-full h-12 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center justify-center gap-2"
                data-testid="upload-attendance-btn"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload & Mark Attendance
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Parks List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-slate-50 p-4 border-b flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Not Visited Parks</h3>
              <p className="text-sm text-slate-500">Parks that were not visited on {formatDateDDMMYYYY(selectedDate)}</p>
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-600">Loading...</p>
            </div>
          ) : notVisitedParks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
              <p className="font-semibold text-green-600">All parks visited!</p>
              <p className="text-sm">No pending attendance for this date</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-bold">Zone</th>
                    <th className="text-left p-3 font-bold">Division</th>
                    <th className="text-left p-3 font-bold">Park Name</th>
                    <th className="text-left p-3 font-bold">Supervisor Assigned</th>
                    <th className="text-center p-3 font-bold">Employees</th>
                    <th className="text-center p-3 font-bold">Attendance Status</th>
                  </tr>
                </thead>
                <tbody>
                  {notVisitedParks.map((park, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-medium">{park.zone}</td>
                      <td className="p-3">{park.division || '-'}</td>
                      <td className="p-3">
                        <div className="font-medium">{park.park_name}</div>
                        <div className="text-xs text-slate-500">{park.park_id}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{park.assigned_supervisor_name || '-'}</div>
                        {park.assigned_supervisor_id && (
                          <div className="text-xs text-slate-500">{park.assigned_supervisor_id}</div>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                          {park.employee_count} employees
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {park.attendance_marked ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center justify-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Marked
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center justify-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingAttendance;
